import { Box, Collapse, Stack, Typography } from "@mui/material";
import Grid from "@mui/material/Grid2";
import { format, isValid } from "date-fns";
import { utcToZonedTime } from "date-fns-tz";
import OutlinedButton from "@/components/common/buttons/outlinedButton";
import TextButton from "@/components/common/buttons/textButton";
import { DATE_TIME_FORMATS, TIME_FORMATS } from "@/config";
import { BLACK } from "@/config/mui/colorPalette";
import { useClientBookingContext } from "@/contexts/clientBookingContext";
import {
  useConfirmReEnterPhoneNumber,
  useLeaveSelectiveBookingLink,
} from "@/hooks/booking/useBookingConfirm";
import { AppointmentType } from "@/types";
import { BookingStep } from "@/types/bookingTypes";
import { formattedPrice } from "@/utils";
import { formattedTime } from "@/utils/date";
import { formatPhoneNumber } from "@/utils/formatPhoneNumber";
import { CamIcon } from "../common/icons";
import InfoCard from "../common/infoCard/infoCard";
import SectionHeader from "../common/sectionHeader/sectionHeader";
import ServiceDetails from "./serviceDetails";

const isClientDetailsStep = (step: BookingStep) => {
  return [
    BookingStep.RETURNING_CLIENT,
    BookingStep.RETURNING_CLIENT_EDIT_DETAILS,
    BookingStep.RETURNING_CLIENT_EDIT_CARD,
    BookingStep.CLIENT_DETAILS,
    BookingStep.FRONT_DESK_BOOKING,
    BookingStep.COLLECT_CREDIT_CARD_INFO,
  ].includes(step);
};

const isValidDate = (dateStr: string) => {
  const date = new Date(dateStr);
  return isValid(date);
};

export default function BookingSummary() {
  const {
    selectedServices,
    selectedProvider,
    timeSlot,
    step,
    setStep,
    shouldChooseProvider,
    medspaData,
    clientDetails,
    clientAccessToken,
    showServicePriceOnBooking,
    isSelectiveBookingLink,
  } = useClientBookingContext();

  const getConfirmReEnterPhoneNumber = useConfirmReEnterPhoneNumber();
  const getConfirmLeaveSelectiveBooking = useLeaveSelectiveBookingLink();

  const providerDisplayName = selectedProvider
    ? `${selectedProvider.user.firstName} ${selectedProvider.user.lastName}`
    : "";
  const medspaTimeZone = medspaData.timezone;
  const visitDetails = {
    durationInMinutes: selectedServices.reduce(
      (acc, service) => acc + service.durationInMinutes,
      0
    ),
    price: selectedServices.reduce(
      (acc, service) => {
        if (service.isVariablePricing) {
          acc.includesVaryingPrice = true;
        }
        acc.number += parseFloat(service.price);
        return acc;
      },
      { number: 0, includesVaryingPrice: false }
    ),
    provider: providerDisplayName,
  };

  const { firstName, lastName, phone, email } = clientDetails || {};

  const handleSetStep = async (targetStep: BookingStep) => {
    if (isSelectiveBookingLink) {
      const confirmed = await getConfirmLeaveSelectiveBooking();
      if (!confirmed) return;
    }

    if (isClientDetailsStep(step)) {
      const ok = await getConfirmReEnterPhoneNumber();
      if (ok) {
        setStep(targetStep);
      }
      return;
    }
    setStep(targetStep);
  };

  return (
    <Stack spacing={{ mobile: 1, "ipad-ver": 2 }}>
      <Collapse
        in={step !== BookingStep.SERVICES}
        timeout="auto"
        unmountOnExit
        aria-label="Appointment summary"
      >
        <InfoCard
          title="Appointment summary"
          sx={{
            mb: { mobile: 1, "ipad-ver": 2 },
          }}
        >
          <Grid container spacing={1}>
            <Grid size={3}>
              <Typography sx={{ color: "gray.main" }} noWrap>
                Duration
              </Typography>
            </Grid>
            <Grid size={9}>
              <Typography>
                {formattedTime(visitDetails.durationInMinutes)}
              </Typography>
            </Grid>
            {showServicePriceOnBooking && (
              <>
                <Grid size={3}>
                  <Typography sx={{ color: "gray.main" }}>
                    Total price
                  </Typography>
                </Grid>
                <Grid size={9}>
                  <Typography>
                    {formattedPrice(
                      visitDetails.price.number,
                      visitDetails.price.includesVaryingPrice
                    )}
                  </Typography>
                </Grid>
              </>
            )}
          </Grid>
        </InfoCard>
        <InfoCard sx={{ mb: 0 }}>
          <SectionHeader
            title="Services"
            action={
              step !== BookingStep.BOOKED && (
                <TextButton
                  sx={{ minWidth: "fit-content" }}
                  onClick={() => handleSetStep(BookingStep.SERVICES)}
                >
                  Edit
                </TextButton>
              )
            }
          />
          <Stack spacing={1.5}>
            {selectedServices.map((service) => {
              const isTelehealth =
                service.appointmentType === AppointmentType.TELEHEALTH;

              return (
                <Box key={service.id}>
                  <Stack
                    sx={{
                      flexDirection: "row",
                      gap: 0.5,
                    }}
                  >
                    {isTelehealth && <CamIcon color={BLACK} size={22} />}
                    <Typography variant="subtitleDefault">
                      {service.name}
                    </Typography>
                  </Stack>
                  <ServiceDetails service={service} />
                </Box>
              );
            })}
          </Stack>
        </InfoCard>
      </Collapse>
      <Collapse
        in={
          ![
            BookingStep.SERVICES,
            BookingStep.AVAILABILITY_AND_PROVIDER_SELECTION,
          ].includes(step)
        }
        timeout="auto"
        unmountOnExit
        aria-label="Provider"
      >
        <InfoCard>
          <SectionHeader
            title="Provider"
            action={
              step !== BookingStep.BOOKED &&
              shouldChooseProvider && (
                <TextButton
                  sx={{ minWidth: "fit-content" }}
                  onClick={() =>
                    handleSetStep(
                      BookingStep.AVAILABILITY_AND_PROVIDER_SELECTION
                    )
                  }
                >
                  Edit
                </TextButton>
              )
            }
          />
          <Grid container spacing={1}>
            <Grid size={3}>
              <Typography sx={{ color: "gray.main" }} noWrap>
                Provider
              </Typography>
            </Grid>
            <Grid size={9}>
              <Typography>{visitDetails.provider}</Typography>
            </Grid>
          </Grid>
        </InfoCard>
      </Collapse>
      <Collapse
        in={
          ![
            BookingStep.SERVICES,
            BookingStep.AVAILABILITY_AND_PROVIDER_SELECTION,
          ].includes(step)
        }
        timeout="auto"
        unmountOnExit
        aria-label="Date and time"
      >
        <InfoCard>
          <SectionHeader
            title="Date and time"
            action={
              step !== BookingStep.BOOKED && (
                <TextButton
                  sx={{ minWidth: "fit-content" }}
                  onClick={() =>
                    handleSetStep(
                      BookingStep.AVAILABILITY_AND_PROVIDER_SELECTION
                    )
                  }
                >
                  Edit
                </TextButton>
              )
            }
          />
          {!!timeSlot?.startStr && isValidDate(timeSlot.startStr) ? (
            <>
              <Typography
                variant="subtitleDefault"
                sx={{
                  mb: 0.5,
                }}
              >
                {format(new Date(timeSlot.startStr), DATE_TIME_FORMATS.BOOKING)}
              </Typography>
              <Typography
                variant="paragraphSmall"
                sx={{
                  color: "gray.main",
                }}
              >
                {`${format(
                  utcToZonedTime(new Date(timeSlot.startStr), medspaTimeZone),
                  TIME_FORMATS.BOOKING
                )} -
              ${format(
                utcToZonedTime(new Date(timeSlot.endStr), medspaTimeZone),
                TIME_FORMATS.BOOKING
              )} (${medspaTimeZone.replace("_", " ")})`}
              </Typography>
            </>
          ) : null}
        </InfoCard>
      </Collapse>
      <Collapse
        in={
          ![
            BookingStep.SERVICES,
            BookingStep.AVAILABILITY_AND_PROVIDER_SELECTION,
            BookingStep.CLIENT_DETAILS,
            BookingStep.RETURNING_CLIENT,
            BookingStep.RETURNING_CLIENT_EDIT_CARD,
            BookingStep.RETURNING_CLIENT_EDIT_DETAILS,
            BookingStep.PHONE_NUMBER,
            BookingStep.CODE_INPUT,
          ].includes(step)
        }
        timeout="auto"
        unmountOnExit
        aria-label="Client"
      >
        <InfoCard>
          <SectionHeader
            title="Client"
            action={
              step !== BookingStep.BOOKED && (
                <TextButton
                  sx={{ minWidth: "fit-content" }}
                  onClick={() => setStep(BookingStep.CLIENT_DETAILS)}
                >
                  Edit
                </TextButton>
              )
            }
          />
          <Grid container spacing={1}>
            <Grid size={3}>
              <Typography sx={{ color: "gray.main" }} noWrap>
                Name
              </Typography>
            </Grid>
            <Grid size={9}>
              <Typography>
                {firstName} {lastName}
              </Typography>
            </Grid>
            <Grid size={3}>
              <Typography sx={{ color: "gray.main" }} noWrap>
                Phone
              </Typography>
            </Grid>
            <Grid size={9}>
              <Typography>{formatPhoneNumber(phone)}</Typography>
            </Grid>
            <Grid size={3}>
              <Typography sx={{ color: "gray.main" }} noWrap>
                Email
              </Typography>
            </Grid>
            <Grid size={9}>
              <Typography sx={{ wordBreak: "break-word" }}>{email}</Typography>
            </Grid>
          </Grid>
        </InfoCard>
      </Collapse>
      <Collapse in={step === BookingStep.BOOKED && !!clientAccessToken}>
        <OutlinedButton
          link={`/client/${medspaData.slug}/appointments`}
          text="View my appointments"
          withIcon={false}
        />
      </Collapse>
    </Stack>
  );
}
